import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { cn } from '@/lib/utils';

interface MarkdownReaderProps {
  content: string;
  className?: string;
  variant?: 'default' | 'compact' | 'prose';
}

const MarkdownReader: React.FC<MarkdownReaderProps> = ({
  content,
  className,
  variant = 'default'
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Base classes for different variants
  const variantClasses = {
    default: "prose prose-sm prose-slate dark:prose-invert max-w-none",
    compact: "prose prose-xs prose-slate dark:prose-invert max-w-none",
    prose: "prose prose-lg prose-slate dark:prose-invert max-w-none"
  };

  // Modern reports use the canonical `## REFERENCES` block (URL/Title/
  // Mentioned In records) — no inline `[doc:N]` / `[web:N]` markers — so no
  // pre-processing is needed. Legacy stored reports that still contain those
  // markers will simply render as plain text, which is fine.
  const processedContent = content ?? '';

  // Handle citation link clicks with event delegation
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if clicked element is a citation link or its parent is
      const citationLink = target.closest('.citation-link') as HTMLElement;

      if (citationLink) {
        e.preventDefault();
        e.stopPropagation();

        const citationId = citationLink.getAttribute('data-citation');

        if (citationId) {
          // Find the target element with the matching id
          const targetElement = container.querySelector(`#${citationId}`);

          if (targetElement) {
            // Scroll to the target
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Highlight briefly
            targetElement.classList.add('bg-yellow-200', 'dark:bg-yellow-900', 'transition-colors', 'duration-300');
            setTimeout(() => {
              targetElement.classList.remove('bg-yellow-200', 'dark:bg-yellow-900');
            }, 2000);
          } else {
            console.warn(`Citation target not found: ${citationId}`);
          }
        }
      }
    };

    // Use capture phase to intercept before browser navigation
    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, []);

  return (
    <div ref={containerRef} className={cn(variantClasses[variant], 'break-words', className)}>
      <style>{`
        .citation-link {
          color: rgb(59 130 246);
          text-decoration: none;
          font-size: 0.85em;
          vertical-align: super;
          cursor: pointer;
          transition: color 0.2s;
          font-weight: 500;
        }
        .citation-link:hover {
          color: rgb(37 99 235);
          text-decoration: underline;
        }
        .dark .citation-link {
          color: rgb(96 165 250);
        }
        .dark .citation-link:hover {
          color: rgb(147 197 253);
        }
        .citation-target {
          display: inline-block;
          transition: background-color 0.3s;
        }
      `}</style>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]} // Support GitHub Flavored Markdown (tables, strikethrough, task lists, etc)
        rehypePlugins={[rehypeRaw]}  // Support HTML within markdown
        components={{
          // Headings with proper hierarchy and spacing
          h1: ({ children, ...props }) => (
            <h1 className="text-2xl font-bold mt-8 mb-4 text-foreground border-b border-border pb-2" {...props}>
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2 className="text-xl font-bold mt-6 mb-3 text-foreground" {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 className="text-lg font-semibold mt-5 mb-2 text-foreground" {...props}>
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 className="text-base font-semibold mt-4 mb-2 text-foreground" {...props}>
              {children}
            </h4>
          ),
          h5: ({ children, ...props }) => (
            <h5 className="text-sm font-semibold mt-3 mb-1 text-foreground" {...props}>
              {children}
            </h5>
          ),
          h6: ({ children, ...props }) => (
            <h6 className="text-xs font-semibold mt-2 mb-1 text-foreground" {...props}>
              {children}
            </h6>
          ),
          
          // Paragraphs with proper spacing
          p: ({ children, ...props }) => (
            <p className="mb-4 text-muted-foreground leading-relaxed" {...props}>
              {children}
            </p>
          ),
          
          // Lists with proper styling
          ul: ({ children, ...props }) => (
            <ul className="list-disc list-inside mb-4 space-y-2 text-muted-foreground pl-4" {...props}>
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol className="list-decimal list-inside mb-4 space-y-2 text-muted-foreground pl-4" {...props}>
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => (
            <li className="text-muted-foreground" {...props}>
              {children}
            </li>
          ),
          
          // Strong and emphasis
          strong: ({ children, ...props }) => (
            <strong className="font-semibold text-foreground" {...props}>
              {children}
            </strong>
          ),
          em: ({ children, ...props }) => (
            <em className="italic text-muted-foreground" {...props}>
              {children}
            </em>
          ),
          
          // Code blocks and inline code
          code: ({ children, inline, ...props }) => {
            if (inline) {
              return (
                <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className="block bg-muted p-4 rounded-lg text-sm font-mono text-foreground overflow-x-auto mb-4" {...props}>
                {children}
              </code>
            );
          },
          
          // Blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote className="border-l-4 border-border pl-4 italic text-muted-foreground mb-4 bg-muted/30 py-2" {...props}>
              {children}
            </blockquote>
          ),
          
          // Horizontal rules
          hr: ({ ...props }) => (
            <hr className="my-8 border-border" {...props} />
          ),
          
          // Links
          a: ({ children, href, ...props }) => (
            <a 
              className="text-primary underline hover:text-primary/80 transition-colors" 
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          ),
          
          // Tables
          table: ({ children, ...props }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border-collapse border border-border" {...props}>
                {children}
              </table>
            </div>
          ),
          thead: ({ children, ...props }) => (
            <thead className="bg-muted/50" {...props}>
              {children}
            </thead>
          ),
          tbody: ({ children, ...props }) => (
            <tbody {...props}>
              {children}
            </tbody>
          ),
          tr: ({ children, ...props }) => (
            <tr className="border-b border-border hover:bg-muted/30 transition-colors" {...props}>
              {children}
            </tr>
          ),
          th: ({ children, ...props }) => (
            <th className="border border-border px-4 py-2 text-left font-semibold text-foreground" {...props}>
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td className="border border-border px-4 py-2 text-muted-foreground" {...props}>
              {children}
            </td>
          ),
          
          // Images
          img: ({ src, alt, ...props }) => (
            <img 
              className="max-w-full h-auto rounded-lg shadow-sm mb-4" 
              src={src} 
              alt={alt} 
              {...props} 
            />
          ),
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownReader;